import { useGetTicketOptions } from '../../openapi/ticket-options/ticket-options';
import { useGetTicketTypes } from '../../openapi/ticket-types/ticket-types';
import { formatCurrency, formatDateWithWeekDay } from '../helpers/formatters';
import { useLang } from '../hooks/useLanguage';
import Tooltip from '../components/Tooltip';
import { useCart } from '../hooks/useCart';
import { calculateOnNet, definePrice } from '../helpers/calculateShowFees';
import { useEffect, useMemo, useRef } from 'react';
import { buildGa4ItemsFromTicketMatrix, sumGa4ItemsValue } from '../helpers/ga4Items';
import { useMarketing } from '../hooks/useMarketing';
import PriceLoader from '../components/PriceLoader';

function TicketInfo({ show }) {
    const { getI18n, t } = useLang();
    const {
        data: { ticketTypes } = { ticketTypes: [] },
        isLoading,
        isError,
        error,
    } = useGetTicketTypes({ optionalId: show.id, onlyShowInList: true, onlyGuestVisible: true });
    const {
        data: { ticketOptions } = { ticketOptions: [] },
        isLoading: isTicketOptionsLoading,
        isError: isTicketOptionsError,
        error: ticketOptionsError,
    } = useGetTicketOptions({ optionalId: show.id, onlyGuestVisible: true });
    const { timeslot, priceOverrides, isPricingLoading } = useCart();
    const { viewItem } = useMarketing();
    const lastFiredTimeslotId = useRef<number | undefined>(undefined);

    const ga4Items = useMemo(
        () => buildGa4ItemsFromTicketMatrix(ticketTypes, ticketOptions, show),
        [ticketTypes, ticketOptions, show],
    );

    useEffect(() => {
        if (!timeslot || ga4Items.length === 0) return;
        if (lastFiredTimeslotId.current === timeslot.id) return;
        lastFiredTimeslotId.current = timeslot.id;
        viewItem({
            currency: 'USD',
            value: sumGa4ItemsValue(ga4Items),
            items: ga4Items,
        });
    }, [timeslot, ga4Items, viewItem]);

    if (isLoading || isTicketOptionsLoading) {
        return <div>{t('common.loading')}</div>;
    }
    if (isError || isTicketOptionsError) {
        return <div>{error?.message || ticketOptionsError?.message}</div>;
    }

    return (
        <div className="fireknife-ticket-info">
            <div className="fireknife-ticket-info__header">
                <h2 className="fireknife-ticket-info__title">{t('widgetSidebarSteps.tickets')}</h2>

                <Tooltip
                    className="fireknife-ticket-info__tooltip"
                    ariaLabel="More information about ticket types"
                >
                    {ticketOptions.map((option) => (
                        <div key={option.name}>
                            <strong>{getI18n(option.name, option.nameI18n)}</strong>{' '}
                            {getI18n(option.description, option.descriptionI18n)}
                            <br />
                        </div>
                    ))}
                </Tooltip>
            </div>

            <div className="fireknife-ticket-info__desc">
                {timeslot?.startTime
                    ? `${t('widgetInformations.pricesFor')} ${formatDateWithWeekDay(timeslot.startTime, t('widgetLocale'))}`
                    : t('widgetInformations.pricesForSelectedDateAndTime')}
            </div>

            <div className="fireknife-ticket-info__dl-wrapper">
                {ticketTypes.map((ticketType: any, idx: number) => (
                    <div key={idx} className="fireknife-select-tickets__boxes">
                        <h3 className="fireknife-select-tickets__box-title">
                            {getI18n(ticketType.name, ticketType.nameI18n)}
                        </h3>
                        <h5 className="fireknife-select-tickets__box-subtitle">
                            {getI18n(ticketType.description, ticketType.descriptionI18n)}
                        </h5>
                        {ticketOptions.map((ticketOption: any) => {
                            let ticketDisplay = 0;
                            try {
                                // Active price for the selected timeslot: dynamic/
                                // timeslot override when present, else show default.
                                // Matches what step 2 and the cart show.
                                const override = priceOverrides.get(
                                    `${ticketType.id}:${ticketOption.id}`,
                                );
                                const netPrice =
                                    override !== undefined
                                        ? override
                                        : definePrice(ticketType.priceMatrix, ticketOption.name);
                                if (show.priceListingMode === 'net') {
                                    ticketDisplay = netPrice;
                                } else {
                                    const calcResult = calculateOnNet(netPrice, show.fees, true);
                                    ticketDisplay = calcResult.finalPrice;
                                }
                            } catch (err: Error | unknown) {
                                console.error('Error defining price:', err);
                                return null;
                            }
                            return (
                                <dl className="fireknife-ticket-info__dl" key={ticketOption.name}>
                                    <dt className="fireknife-ticket-info__dt">
                                        {getI18n(ticketOption.name, ticketOption.nameI18n)}
                                    </dt>
                                    <dd className="fireknife-ticket-info__dd">
                                        {isPricingLoading ? (
                                            <PriceLoader />
                                        ) : (
                                            formatCurrency(ticketDisplay / 100)
                                        )}
                                    </dd>
                                </dl>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default TicketInfo;
